import axios from 'axios';
import i18n from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (email, password, name) => {
    const response = await api.post('/auth/register', {
      email,
      password,
      name
    });
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (email, code, newPassword) => {
    const response = await api.post('/auth/reset-password', {
      email,
      code,
      new_password: newPassword
    });
    return response.data;
  }
};

const orderLang = () => ((i18n.language || 'ru').slice(0, 2) === 'en' ? 'en' : 'ru');

export const subscriptionAPI = {
  getTokens: async () => {
    const response = await api.get('/user/tokens');
    return response.data;
  },

  getSubscriptions: async () => {
    const response = await api.get('/subscriptions');
    return response.data;
  },

  // Клиент передаёт только id тарифа или курса — цену и сумму определяет сервер.
  // Ответ: { order, payment: { mode: 'redirect'|'form', url | action+fields } }
  createOrder: async ({ subscriptionId = null, courseId = null, email = null }) => {
    const body = {};
    if (subscriptionId != null) body.subscription_id = subscriptionId;
    if (courseId != null) body.course_id = courseId;
    if (email) body.email = email;
    const response = await api.post('/orders/create', body);
    return response.data;
  },

  getOrder: async (orderId) => {
    const response = await api.get(`/orders/${orderId}`, { params: { lang: orderLang() } });
    return response.data;
  },

  getMyOrders: async () => {
    const response = await api.get('/orders/user/orders', { params: { lang: orderLang() } });
    return response.data;
  },
};

export const adminAPI = {
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  generateUser: async () => {
    const response = await api.post('/admin/users/generate');
    return response.data;
  },

  addTokens: async (userId, amount) => {
    const response = await api.post(`/admin/users/${userId}/tokens`, { amount });
    return response.data;
  },

  setPetrovichAccess: async (userId, petrovichAccess) => {
    const response = await api.post(`/admin/users/${userId}/petrovich-access`, {
      petrovich_access: petrovichAccess
    });
    return response.data;
  },

  listPermissions: async () => {
    const response = await api.get('/admin/permissions');
    return response.data;
  },

  setPermissions: async (userId, permissions) => {
    const response = await api.post(`/admin/users/${userId}/permissions`, { permissions });
    return response.data;
  }
};

export const historyAPI = {
  getHistory: async () => {
    const response = await api.get('/history');
    return response.data;
  },

  downloadFile: async (itemId) => {
    const response = await api.get(`/history/${itemId}/download`, {
      responseType: 'blob'
    });
    return response;
  },

  deleteItem: async (itemId) => {
    const response = await api.delete(`/history/${itemId}`);
    return response.data;
  }
};

export default api;